import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AdminDashboard from 'layouts/AdminDashboard';
import { getSubscription, updateSubscription } from './SubscriptionService';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import Container from 'components/Container';
import dayjs from 'dayjs';

import GroupUserList from './GroupUserList';
import SelectedItemList from './SelectedItemList';
import InvitationList from './InvitationList';
import ScheduleSection from './ScheduleSection';
import { notify } from 'utils/notify';
import { extractErrorMessage } from 'utils/extractErrorMessage';

const SubscriptionDetail = (): JSX.Element => {
  const { membershipid, subscriptionid } = useParams();
  const [subscriptionName, setSubscriptionName] = useState('');
  const [status, setStatus] = useState('');
  const [appId, setAppId] = useState('');
  const [appCode, setAppCode] = useState('');
  //const [subscriptionId, setSubscriptionId] = useState('');
  const [trial, setTrial] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (membershipid && subscriptionid) {
      getSubscription(membershipid, subscriptionid)
        .then((response) => {
          const subscription = response.data;
          setSubscriptionName(subscription.subscriptionName);
          setStartDate(
            dayjs(subscription.startDate).format('DD/MM/YYYY HH:mm'),
          );
          setEndDate(dayjs(subscription.endDate).format('DD/MM/YYYY HH:mm'));
          setStatus(subscription.status);
          setTrial(subscription.trial);
          setAutoRenew(subscription.autoRenew);

          if (subscription.products && subscription.products.length > 0) {
            const firstApp = subscription.products[0]?.apps[0];
            if (firstApp) {
              setAppId(firstApp.id);
              setAppCode(firstApp.appCode);
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching subscription:', error);
          notify(extractErrorMessage(error));
        });
    }
  }, [membershipid, subscriptionid]);

  const handleSave = () => {
    if (!subscriptionid) return;
    setSaving(true);
    updateSubscription(subscriptionid, {
      subscriptionName,
      status,
      trial,
      autoRenew,
    })
      .then(() => notify('Subscription saved.'))
      .catch((error) => notify(extractErrorMessage(error)))
      .finally(() => setSaving(false));
  };

  return (
    <Container>
      <AdminDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Typography variant="h4">Membership Details</Typography>
        </Box>
        <Box marginTop={2} marginBottom={4}>
          <Typography variant="h4">Subscription Detail</Typography>
        </Box>

        <form>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subscription Name"
                variant="outlined"
                fullWidth
                value={subscriptionName}
                onChange={(e) => setSubscriptionName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="App Id"
                variant="outlined"
                fullWidth
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="App Code"
                variant="outlined"
                fullWidth
                value={appCode}
                onChange={(e) => setAppCode(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                variant="outlined"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                variant="outlined"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Status"
                variant="outlined"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={trial}
                    onChange={(e) => setTrial(e.target.checked)}
                  />
                }
                label="Trial"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                  />
                }
                label="Auto Renew"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Grid>
          </Grid>
        </form>
        <Box marginTop={2} marginBottom={0}>
          <GroupUserList membershipId={membershipid} />
        </Box>
        <Box marginTop={2} marginBottom={4}>
          <SelectedItemList appId={appId} subscriptionId={subscriptionid} />
        </Box>
        <Box marginTop={2} marginBottom={4}>
          <InvitationList subscriptionId={subscriptionid} />
        </Box>
        <Box marginTop={2} marginBottom={4}>
          <ScheduleSection appCode={appCode} subscriptionId={subscriptionid} />
        </Box>
      </AdminDashboard>
    </Container>
  );
};

export default SubscriptionDetail;
